export const FRAME_HEADER_BYTES = 4;

/** `uint32 LE seq` header followed by the block as `float32 LE` PCM. */
export function encodeRadioFrame(seq: number, pcm: Float32Array): ArrayBuffer {
  const buffer = new ArrayBuffer(FRAME_HEADER_BYTES + pcm.length * 4);
  const view = new DataView(buffer);
  view.setUint32(0, seq >>> 0, true);
  for (let i = 0; i < pcm.length; i++) {
    view.setFloat32(FRAME_HEADER_BYTES + i * 4, pcm[i], true);
  }
  return buffer;
}

export function decodeRadioFrame(buffer: ArrayBuffer): { seq: number; pcm: Float32Array } | null {
  if (buffer.byteLength < FRAME_HEADER_BYTES || (buffer.byteLength - FRAME_HEADER_BYTES) % 4 !== 0) {
    return null;
  }
  const view = new DataView(buffer);
  const seq = view.getUint32(0, true);
  const count = (buffer.byteLength - FRAME_HEADER_BYTES) / 4;
  const pcm = new Float32Array(count);
  for (let i = 0; i < count; i++) {
    pcm[i] = view.getFloat32(FRAME_HEADER_BYTES + i * 4, true);
  }
  return { seq, pcm };
}

/** Linear-interpolation resampler for when the AudioContext refuses 16 kHz. */
export function resampleLinear(input: Float32Array, fromRate: number, toRate: number): Float32Array {
  if (fromRate === toRate || input.length === 0) return input;
  const ratio = fromRate / toRate;
  const outLength = Math.max(1, Math.round(input.length / ratio));
  const output = new Float32Array(outLength);
  for (let i = 0; i < outLength; i++) {
    const pos = i * ratio;
    const idx = Math.floor(pos);
    const frac = pos - idx;
    const a = input[Math.min(idx, input.length - 1)];
    const b = input[Math.min(idx + 1, input.length - 1)];
    output[i] = a + (b - a) * frac;
  }
  return output;
}

/**
 * Accumulates arbitrary-length audio chunks and emits fixed-size blocks so the
 * VAD sees a constant block duration regardless of the worklet's render quantum.
 */
export class BlockAccumulator {
  private readonly blockSize: number;
  private buffer: Float32Array;
  private filled = 0;

  constructor(blockSize: number) {
    this.blockSize = blockSize;
    this.buffer = new Float32Array(blockSize);
  }

  push(chunk: Float32Array): Float32Array[] {
    const blocks: Float32Array[] = [];
    let offset = 0;
    while (offset < chunk.length) {
      const take = Math.min(this.blockSize - this.filled, chunk.length - offset);
      this.buffer.set(chunk.subarray(offset, offset + take), this.filled);
      this.filled += take;
      offset += take;
      if (this.filled === this.blockSize) {
        blocks.push(this.buffer);
        this.buffer = new Float32Array(this.blockSize);
        this.filled = 0;
      }
    }
    return blocks;
  }

  reset(): void {
    this.filled = 0;
  }
}
